import { BadgeCheck, Search } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import type { TalentProfile } from '../types/domain';
import { InlineSpinner } from './LoadingIndicators';

interface PopoverPosition {
  bottom?: number;
  left: number;
  resultsMaxHeight: number;
  top?: number;
  width: number;
}

interface BrandAmbassadorPickerProps {
  disabled?: boolean;
  error?: boolean;
  idPrefix: string;
  loading: boolean;
  onClear: () => void;
  onPageChange: (page: number) => void;
  onSearchChange: (search: string) => void;
  onSelect: (person: TalentProfile) => void;
  options: TalentProfile[];
  page: number;
  pageCount: number;
  search: string;
  selected: TalentProfile | null;
}

export function BrandAmbassadorPicker({
  disabled = false,
  error = false,
  idPrefix,
  loading,
  onClear,
  onPageChange,
  onSearchChange,
  onSelect,
  options,
  page,
  pageCount,
  search,
  selected,
}: BrandAmbassadorPickerProps) {
  const [open, setOpen] = useState(!selected);
  const [activeIndex, setActiveIndex] = useState(0);
  const [popoverPosition, setPopoverPosition] = useState<PopoverPosition | null>(null);
  const comboboxRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const eligibleOptions = useMemo(
    () => options
      .filter((person) => person.is_assignment_eligible)
      .sort((left, right) => (
        Number(right.verification_status === 'verified') - Number(left.verification_status === 'verified')
        || Number(right.is_available) - Number(left.is_available)
        || left.display_name.localeCompare(right.display_name)
        || left.reference.localeCompare(right.reference)
      )),
    [options],
  );

  const positionPopover = useCallback(() => {
    const anchor = comboboxRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const viewportMargin = 12;
    const gap = 6;
    const spaceBelow = window.innerHeight - rect.bottom - viewportMargin;
    const spaceAbove = rect.top - viewportMargin;
    const opensAbove = spaceBelow < 220 && spaceAbove > spaceBelow;
    const availableHeight = Math.max(140, (opensAbove ? spaceAbove : spaceBelow) - gap);
    const popoverHeight = Math.min(330, availableHeight);
    setPopoverPosition({
      ...(opensAbove
        ? { bottom: window.innerHeight - rect.top + gap }
        : { top: rect.bottom + gap }),
      left: rect.left,
      resultsMaxHeight: Math.max(96, popoverHeight - (pageCount > 1 ? 44 : 0)),
      width: rect.width,
    });
  }, [pageCount]);

  useEffect(() => {
    if (!open || selected) return undefined;
    const closeOnOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!comboboxRef.current?.contains(target) && !popoverRef.current?.contains(target)) {
        setOpen(false);
      }
    };
    const frame = window.requestAnimationFrame(positionPopover);
    document.addEventListener('mousedown', closeOnOutsideClick);
    window.addEventListener('resize', positionPopover);
    window.addEventListener('scroll', positionPopover, true);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener('mousedown', closeOnOutsideClick);
      window.removeEventListener('resize', positionPopover);
      window.removeEventListener('scroll', positionPopover, true);
    };
  }, [open, positionPopover, selected]);

  function choose(person: TalentProfile) {
    onSelect(person);
    setOpen(false);
    setActiveIndex(0);
  }

  function clear() {
    onClear();
    setActiveIndex(0);
    setOpen(true);
    window.requestAnimationFrame(() => searchRef.current?.focus());
  }

  function handleKeyDown(event: ReactKeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
      if (eligibleOptions.length === 0) return;
      setActiveIndex((current) => (
        event.key === 'ArrowDown'
          ? (current + 1) % eligibleOptions.length
          : (current - 1 + eligibleOptions.length) % eligibleOptions.length
      ));
      return;
    }
    if (event.key === 'Enter' && open && eligibleOptions[activeIndex]) {
      event.preventDefault();
      choose(eligibleOptions[activeIndex]);
    }
  }

  if (selected) {
    return (
      <div className="talent-selection">
        <span>
          <strong>{selected.display_name}</strong>
          <small>
            {selected.reference} · {selected.city}
            {selected.verification_status === 'verified' ? ' · BFC verified' : ` · ${selected.verification_status} profile`}
            {!selected.is_available ? ' · marked unavailable' : ''}
          </small>
        </span>
        <button className="text-button" disabled={disabled} onClick={clear} type="button">Change</button>
      </div>
    );
  }

  return (
    <div
      className="talent-combobox"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget) && !popoverRef.current?.contains(event.relatedTarget)) {
          setOpen(false);
        }
      }}
      ref={comboboxRef}
    >
      <label className="talent-search-field">
        <Search aria-hidden="true" size={17} />
        <input
          aria-activedescendant={open && eligibleOptions[activeIndex] ? `${idPrefix}-option-${eligibleOptions[activeIndex].id}` : undefined}
          aria-autocomplete="list"
          aria-controls={`${idPrefix}-results`}
          aria-expanded={open}
          aria-label="Search brand ambassadors"
          disabled={disabled}
          onChange={(event) => {
            onSearchChange(event.target.value);
            setActiveIndex(0);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="Search name, BA reference, city or skill"
          ref={searchRef}
          role="combobox"
          value={search}
        />
      </label>
      {open && popoverPosition ? createPortal(
        <div
          className="talent-combobox-popover"
          ref={popoverRef}
          style={{
            ...(popoverPosition.top === undefined ? {} : { top: popoverPosition.top }),
            ...(popoverPosition.bottom === undefined ? {} : { bottom: popoverPosition.bottom }),
            left: popoverPosition.left,
            width: popoverPosition.width,
          }}
        >
          <div className="talent-search-results" id={`${idPrefix}-results`} role="listbox" style={{ maxHeight: popoverPosition.resultsMaxHeight }}>
            {loading ? <p><InlineSpinner /> Searching talent…</p> : null}
            {error ? <p>Unable to load eligible Brand Ambassadors.</p> : null}
            {!loading && !error && eligibleOptions.length === 0 ? <p>No eligible ambassadors match this search.</p> : null}
            {eligibleOptions.map((person, index) => (
              <button
                aria-selected={index === activeIndex}
                className={index === activeIndex ? 'active' : ''}
                id={`${idPrefix}-option-${person.id}`}
                key={person.id}
                onClick={() => choose(person)}
                onMouseEnter={() => setActiveIndex(index)}
                role="option"
                type="button"
              >
                <span>
                  <strong>{person.display_name}</strong>
                  <small>{person.reference} · {person.city}{!person.is_available ? ' · marked unavailable' : ''}</small>
                </span>
                {person.verification_status === 'verified'
                  ? <span className="talent-result-verified"><BadgeCheck size={14} /> BFC verified</span>
                  : <small>{person.verification_status} profile</small>}
              </button>
            ))}
          </div>
          {pageCount > 1 ? (
            <div className="talent-search-pagination">
              <button className="text-button" disabled={page <= 1} onClick={() => { onPageChange(page - 1); setActiveIndex(0); }} type="button">Previous</button>
              <span>Page {page} of {pageCount}</span>
              <button className="text-button" disabled={page >= pageCount} onClick={() => { onPageChange(page + 1); setActiveIndex(0); }} type="button">Next</button>
            </div>
          ) : null}
        </div>,
        document.body,
      ) : null}
    </div>
  );
}
